import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Box, Card, CardContent, CardMedia, CircularProgress, Typography, TableContainer, Table, TableBody, TableRow, TableCell, Paper, MenuItem} from '@mui/material';
import Button from '../../../../components/Button.js';
import { Link } from 'react-router-dom';
import useFetch from '../../../../hooks/useFetch.js';
import { getAllV2Players, getAllV2Sport } from '../../../../../api/index.js';
import { getUserProvider } from '../../../../../actions/auth.js';
import PlayerDTO from '../../../../DTO/PlayerDTO.js';
import SearchFilterDTO from '../../../../DTO/SearchFilterDTO.js';
import Pagination from '../../../../components/pagination/Pagination.js';
import Sort from '../../../../components/sort/Sort.js';
import SelectFilter from '../../../../components/filter/SelectFilter.js';
import nationalitiesBDD from '../../../../BDD/nationalities.js';

const GetPlayers = () => {

    return <Box sx={{ textAlign : 'center'}}>
            <Button to="/multisport/actors/players/create" component={Link}>Créer un athlète</Button>

            <Players/>
    </Box>
}

const Players = () => {

    const provider = getUserProvider()

    const [searchFilter, setSearchFilter] = useState(new SearchFilterDTO({
        global : {
            provider : provider,
        },
        pagination: {
            offset: 0,
            limit: 10,
        },
        sort: [
                {lastname : 1},  // Champ par défaut pour le tri
            ]
    }))

    const { data, metadata, isLoading, error, fetchData } = useFetch(getAllV2Players, [JSON.stringify(searchFilter)]);

    const { data : sportsData, fetchData : fetchSports} = useFetch(getAllV2Sport, [getUserProvider()]);

    useEffect(() => {
        fetchSports()
    }, [])

    const sports = useMemo(() => {

        if(!sportsData)
            return []

        return sportsData.map(s => s)

    }, [sportsData])

    const disciplines = useMemo(() => {

        if(!sports)
            return []

        return sports.reduce((acc, s) => {

            if(!searchFilter.sport?._id?.length ||
                searchFilter.sport._id.includes(s._id)
            ){
                acc.push(...s.disciplines)
            } else if(searchFilter.discipline && searchFilter.discipline._id) {
                // Sinon, inclure uniquement les disciplines qui sont dans le filtre discipline
                const filteredDisciplines = s.disciplines.filter(d => searchFilter.discipline._id.includes(d._id));
                acc.push(...filteredDisciplines);
              } 

            return acc
        }, [])

    }, [sports, searchFilter])

    const nationalities = useMemo(() => {
        return nationalitiesBDD.sort((a, b) => a.localeCompare(b)).map(n => ({_id : n , label : n}))
    }, [])
    
    useEffect(() => {
        fetchData()
    }, [searchFilter])

    const handlePageChange = (newOffset) => {
        setSearchFilter((prevFilter) => ({
          ...prevFilter,
          pagination: {
            ...prevFilter.pagination,
            offset: newOffset
          }
        }));
    };

    const handleLimitChange = (newLimit) => {
        setSearchFilter((prevFilter) => ({
          ...prevFilter,
          pagination: {
            offset: 0, // Reset to the first page
            limit: newLimit
          }
        }));
      };

    const handleSortChange = (newSort) => {
        setSearchFilter((prevFilter) => ({
            ...prevFilter,
            sort: newSort
        }));
    };

    if(isLoading)
        return <CircularProgress sx={{mt : 3}}/>

    if(!data)
        return <Typography sx={{m : 3}}>Erreur</Typography>

    return  <Box sx={{mt : 2}}>
                <Box sx={{display : 'flex', justifyContent : 'center'}}>
                    <SelectFilter
                        label="Sport"
                        items={sports}
                        keyField="_id"
                        labelField="label"
                        mapping="sport._id"
                        searchFilter={searchFilter}
                        setSearchFilter={setSearchFilter}
                    />
                    <SelectFilter
                        label="Discipline"
                        items={disciplines}
                        keyField="_id"
                        labelField="label"
                        mapping="discipline._id"
                        searchFilter={searchFilter}
                        setSearchFilter={setSearchFilter}
                    />

                    <SelectFilter
                        label="Nationnalité"
                        items={nationalities}
                        keyField="_id"
                        labelField="label"
                        mapping="global.nationality"
                        searchFilter={searchFilter}
                        setSearchFilter={setSearchFilter}
                    />
                </Box>
                
                {
                    data.length ? 
                        <>
                            <Pagination
                                offset={searchFilter?.pagination?.offset}
                                limit={searchFilter?.pagination?.limit}
                                totalResults={metadata.totalResults}  // Assurez-vous que votre réponse inclut ce champ
                                onPageChange={handlePageChange}
                                onLimitChange={handleLimitChange}
                            />
                            <Sort sort={searchFilter.sort} onSortChange={handleSortChange}>
                                <MenuItem value="lastname">Nom</MenuItem>
                                <MenuItem value="birthdate">Date de naissance</MenuItem>
                            </Sort>
                            {data.map(player => <Player player={new PlayerDTO(player)}></Player>)}
                            <Pagination
                                offset={searchFilter?.pagination?.offset}
                                limit={searchFilter?.pagination?.limit}
                                totalResults={metadata.totalResults}  // Assurez-vous que votre réponse inclut ce champ
                                onPageChange={handlePageChange}
                                onLimitChange={handleLimitChange}
                            />
                        </>
                            : 
                        <Typography sx={{m : 3}}>Aucun athlète</Typography>
                }
            </Box>
}

const Player = ({player}) => {

    // Transformation des données
    const sportsWithDisciplines = player.disciplines.reduce((acc, discipline) => {
        const { sport, idName } = discipline;
        // Si le sport n'existe pas déjà dans l'accumulateur, l'ajouter
        if (!acc[sport.idName]) {
            acc[sport.idName] = {
                sportName: sport.label,
                disciplines: []
            };
        }
        // Ajouter la discipline à la liste des disciplines du sport
        acc[sport.idName].disciplines.push(idName);
        return acc;
    }, {});

    return <Card sx={{maxWidth : 300, display : 'inline-block', m : 2, p : .3}}>
        <CardMedia image={undefined} title={"Athlète"}/>
            <CardContent>
                <Box style={{padding:'1em'}}>
                    <Typography variant="subtitle1" style={{fontWeight:'bolder'}}>{player.lastname} {player.firstname}</Typography>
                    {player.birthdate && <Typography variant="subtitle2">{format(new Date(player.birthdate), "dd-MM-yyyy'")}</Typography>}
                    {player.nationality && <Typography variant="subtitle2">{player.nationality}</Typography>}
                </Box>
            {/* Afficher les sports et leurs disciplines */}
            <Box style={{padding:'1em'}}>
                <TableContainer component={Paper}>
                    <Table size="small" aria-label="sports table">
                    <TableBody>
                            {Object.values(sportsWithDisciplines).map(({ sportName, disciplines }) => (
                                <TableRow key={sportName}>
                                    <TableCell component="th" scope="row">
                                        <Typography variant="subtitle2" style={{fontWeight:'bolder'}}>{sportName}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="subtitle2" sx={{
                                                                            maxWidth: 100,
                                                                            whiteSpace: 'nowrap',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis'
                                                                        }}>{disciplines.join(', ')}</Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                </Box>
            </CardContent>
            <Box style={{padding:'1em'}}>
                <Button component={Link} to={"create/"+player._id}>Editer</Button>
            </Box>
        </Card>
}

export default GetPlayers